import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import {
  createGeoAdminDistrict,
  createGeoAdminMandal,
  createGeoAdminVillage,
  deleteGeoAdminDistrict,
  deleteGeoAdminMandal,
  deleteGeoAdminVillage,
  updateGeoAdminDistrict,
  updateGeoAdminMandal,
  updateGeoAdminVillage,
} from "../api/geoDirectoryAdminApi";
import { useGeoDirectory } from "../context/GeoDirectoryContext";
import { useToast } from "../context/ToastContext";
import type { TgRegistrationDistrictRow, TgRegistrationMandal, TgRegistrationVillage } from "../data/geoDirectoryStore";

type GeoDirTab = "districts" | "mandals" | "villages";

function districtPrimaryKey(d: TgRegistrationDistrictRow): string | number {
  if (d.id !== undefined && d.id !== null) return d.id;
  return d.registrationDistrictCode;
}

function DistrictEditorCard({
  d,
  onChanged,
}: {
  d: TgRegistrationDistrictRow;
  onChanged: () => Promise<void>;
}) {
  const { showToast } = useToast();
  const [districtKey, setDistrictKey] = useState(d.districtKey);
  const [code, setCode] = useState(d.registrationDistrictCode);
  const [name, setName] = useState(d.registrationDistrictName);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setDistrictKey(d.districtKey);
    setCode(d.registrationDistrictCode);
    setName(d.registrationDistrictName);
  }, [d.id, d.districtKey, d.registrationDistrictCode, d.registrationDistrictName]);

  const pk = districtPrimaryKey(d);

  return (
    <article className="geo-directory-master__card">
      <span className="geo-directory-master__card-id" title="District primary key (registration district code)">
        #{pk}
      </span>
      <div className="geo-directory-master__card-fields geo-directory-master__card-fields--district">
        <div className="field">
          <label htmlFor={`gdist-k-${pk}`}>District key</label>
          <input
            id={`gdist-k-${pk}`}
            value={districtKey}
            onChange={(e) => setDistrictKey(e.target.value)}
            maxLength={64}
          />
        </div>
        <div className="field">
          <label htmlFor={`gdist-c-${pk}`}>Registration code</label>
          <input id={`gdist-c-${pk}`} value={code} onChange={(e) => setCode(e.target.value)} maxLength={32} />
        </div>
        <div className="field">
          <label htmlFor={`gdist-n-${pk}`}>District name</label>
          <input id={`gdist-n-${pk}`} value={name} onChange={(e) => setName(e.target.value)} maxLength={255} />
        </div>
      </div>
      <div className="geo-directory-master__card-foot">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={busy || !districtKey.trim() || !code.trim() || !name.trim()}
          onClick={() => {
            void (async () => {
              setBusy(true);
              try {
                await updateGeoAdminDistrict(pk, {
                  districtKey: districtKey.trim(),
                  registrationDistrictCode: code.trim(),
                  registrationDistrictName: name.trim(),
                });
                showToast("District saved", "success");
                await onChanged();
              } catch (e) {
                showToast(e instanceof Error ? e.message : String(e), "error");
              } finally {
                setBusy(false);
              }
            })();
          }}
        >
          Save
        </button>
        <button
          type="button"
          className="btn btn--link btn--sm"
          disabled={busy}
          onClick={() => {
            if (!window.confirm(`Delete district “${d.registrationDistrictName}” and all mandals and villages under it?`))
              return;
            void (async () => {
              setBusy(true);
              try {
                await deleteGeoAdminDistrict(pk);
                showToast("District removed", "success");
                await onChanged();
              } catch (e) {
                showToast(e instanceof Error ? e.message : String(e), "error");
              } finally {
                setBusy(false);
              }
            })();
          }}
        >
          Delete
        </button>
      </div>
    </article>
  );
}

function MandalEditorCard({
  m,
  districtId,
  onChanged,
}: {
  m: TgRegistrationMandal;
  districtId: string | number;
  onChanged: () => Promise<void>;
}) {
  const { showToast } = useToast();
  const [mandalCode, setMandalCode] = useState(m.mandalCode);
  const [mandalName, setMandalName] = useState(m.mandalName);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setMandalCode(m.mandalCode);
    setMandalName(m.mandalName);
  }, [m.id, m.mandalCode, m.mandalName]);

  return (
    <article className="geo-directory-master__card">
      <span className="geo-directory-master__card-id" title="Database id">
        #{m.id}
      </span>
      <div className="geo-directory-master__card-fields geo-directory-master__card-fields--mandal">
        <div className="field">
          <label htmlFor={`gman-c-${m.id}`}>Mandal code</label>
          <input id={`gman-c-${m.id}`} value={mandalCode} onChange={(e) => setMandalCode(e.target.value)} maxLength={64} />
        </div>
        <div className="field">
          <label htmlFor={`gman-n-${m.id}`}>Mandal name</label>
          <input id={`gman-n-${m.id}`} value={mandalName} onChange={(e) => setMandalName(e.target.value)} maxLength={255} />
        </div>
      </div>
      <div className="geo-directory-master__card-foot">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={busy || !mandalCode.trim() || !mandalName.trim()}
          onClick={() => {
            void (async () => {
              setBusy(true);
              try {
                await updateGeoAdminMandal(m.id, {
                  districtId,
                  mandalCode: mandalCode.trim(),
                  mandalName: mandalName.trim(),
                });
                showToast("Mandal saved", "success");
                await onChanged();
              } catch (e) {
                showToast(e instanceof Error ? e.message : String(e), "error");
              } finally {
                setBusy(false);
              }
            })();
          }}
        >
          Save
        </button>
        <button
          type="button"
          className="btn btn--link btn--sm"
          disabled={busy}
          onClick={() => {
            if (!window.confirm(`Delete mandal “${m.mandalName}” and all villages under it?`)) return;
            void (async () => {
              setBusy(true);
              try {
                await deleteGeoAdminMandal(m.id);
                showToast("Mandal removed", "success");
                await onChanged();
              } catch (e) {
                showToast(e instanceof Error ? e.message : String(e), "error");
              } finally {
                setBusy(false);
              }
            })();
          }}
        >
          Delete
        </button>
      </div>
    </article>
  );
}

function VillageEditorCard({
  v,
  mandalId,
  onChanged,
}: {
  v: TgRegistrationVillage;
  mandalId: number;
  onChanged: () => Promise<void>;
}) {
  const { showToast } = useToast();
  const [villageName, setVillageName] = useState(v.name);
  const [villageCode, setVillageCode] = useState(v.code);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setVillageName(v.name);
    setVillageCode(v.code);
  }, [v.id, v.name, v.code]);

  return (
    <article className="geo-directory-master__card">
      <span className="geo-directory-master__card-id" title="Database id">
        #{v.id}
      </span>
      <div className="geo-directory-master__card-fields geo-directory-master__card-fields--village">
        <div className="field">
          <label htmlFor={`gvill-n-${v.id}`}>Village name</label>
          <input id={`gvill-n-${v.id}`} value={villageName} onChange={(e) => setVillageName(e.target.value)} maxLength={255} />
        </div>
        <div className="field">
          <label htmlFor={`gvill-c-${v.id}`}>Village code</label>
          <input id={`gvill-c-${v.id}`} value={villageCode} onChange={(e) => setVillageCode(e.target.value)} maxLength={64} />
        </div>
      </div>
      <div className="geo-directory-master__card-foot">
        <button
          type="button"
          className="btn btn--primary btn--sm"
          disabled={busy || !villageName.trim() || !villageCode.trim()}
          onClick={() => {
            void (async () => {
              setBusy(true);
              try {
                await updateGeoAdminVillage(v.id, {
                  mandalId,
                  villageName: villageName.trim(),
                  villageCode: villageCode.trim(),
                });
                showToast("Village saved", "success");
                await onChanged();
              } catch (e) {
                showToast(e instanceof Error ? e.message : String(e), "error");
              } finally {
                setBusy(false);
              }
            })();
          }}
        >
          Save
        </button>
        <button
          type="button"
          className="btn btn--link btn--sm"
          disabled={busy}
          onClick={() => {
            if (!window.confirm(`Delete village “${v.name}”?`)) return;
            void (async () => {
              setBusy(true);
              try {
                await deleteGeoAdminVillage(v.id);
                showToast("Village removed", "success");
                await onChanged();
              } catch (e) {
                showToast(e instanceof Error ? e.message : String(e), "error");
              } finally {
                setBusy(false);
              }
            })();
          }}
        >
          Delete
        </button>
      </div>
    </article>
  );
}

export function GeoDirectoryMasterPage() {
  const { store, loading, refetch } = useGeoDirectory();
  const { showToast } = useToast();
  const [tab, setTab] = useState<GeoDirTab>("districts");

  const districts = useMemo(() => {
    if (!store) return [];
    return [...store.tgRegistrationDistricts()].sort((a, b) =>
      a.registrationDistrictName.localeCompare(b.registrationDistrictName),
    );
  }, [store]);

  const canEditRows = useMemo(() => {
    if (!store || districts.length === 0) return true;
    const id = districts[0].id;
    return typeof id === "number" || typeof id === "string";
  }, [store, districts]);

  const [selectedDistrictId, setSelectedDistrictId] = useState<string | number | "">("");
  const [selectedMandalId, setSelectedMandalId] = useState<number | "">("");

  useEffect(() => {
    if (districts.length === 0) {
      setSelectedDistrictId("");
      setSelectedMandalId("");
      return;
    }
    setSelectedDistrictId((prev) => {
      const first = districts[0];
      const firstPk = districtPrimaryKey(first);
      if (prev === "" || !districts.some((d) => districtPrimaryKey(d) === prev)) return firstPk;
      return prev;
    });
  }, [districts]);

  const selectedDistrict = useMemo(
    () =>
      selectedDistrictId === "" ? undefined : districts.find((d) => districtPrimaryKey(d) === selectedDistrictId),
    [districts, selectedDistrictId],
  );

  const mandalsSorted = useMemo(() => {
    if (!selectedDistrict?.mandals) return [];
    return [...selectedDistrict.mandals].sort((a, b) => a.mandalName.localeCompare(b.mandalName));
  }, [selectedDistrict]);

  useEffect(() => {
    if (mandalsSorted.length === 0) {
      setSelectedMandalId("");
      return;
    }
    setSelectedMandalId((prev) => {
      if (prev === "" || !mandalsSorted.some((m) => m.id === prev)) return mandalsSorted[0].id;
      return prev;
    });
  }, [mandalsSorted]);

  const selectedMandal = useMemo(
    () => (selectedMandalId === "" ? undefined : mandalsSorted.find((m) => m.id === selectedMandalId)),
    [mandalsSorted, selectedMandalId],
  );

  const villagesSorted = useMemo(() => {
    if (!selectedMandal?.villages) return [];
    return [...selectedMandal.villages].sort((a, b) => a.name.localeCompare(b.name));
  }, [selectedMandal]);

  const onChanged = useCallback(async () => {
    await refetch();
  }, [refetch]);

  const [newDistrictKey, setNewDistrictKey] = useState("");
  const [newDistrictCode, setNewDistrictCode] = useState("");
  const [newDistrictName, setNewDistrictName] = useState("");
  const [newMandalCode, setNewMandalCode] = useState("");
  const [newMandalName, setNewMandalName] = useState("");
  const [newVillageName, setNewVillageName] = useState("");
  const [newVillageCode, setNewVillageCode] = useState("");

  const tabIds = { districts: "geo-tab-districts", mandals: "geo-tab-mandals", villages: "geo-tab-villages" } as const;

  return (
    <div className="geo-directory-master">
      <PageHeader
        breadcrumb={[{ label: "Home", to: "/" }, { label: "Geo directory" }]}
        title="Districts, mandals & villages"
        subtitle="Maintain the geographic directory used across applicants, zones, and reports. Data is stored by the API (SQLite or MySQL when configured)."
      />

      <p className="geo-directory-master__lede text-muted">
        Use the tabs to focus on one level at a time. Choose <strong>Districts</strong> first, then <strong>Mandals</strong> for a district, then{" "}
        <strong>Villages</strong> for a mandal.{" "}
        <Link to="/reports">Area reports</Link> read from the same directory.
      </p>

      {loading && !store ? (
        <p className="geo-directory-master__loading section-lead text-muted">Loading directory…</p>
      ) : !store || districts.length === 0 ? (
        <div className="help-banner help-banner--muted geo-directory-master__alert">
          <strong>No districts in context</strong>
          <p className="geo-directory-master__alert-p">
            Open the <strong>Districts</strong> tab and add a record (the server must be reachable; the offline bundle is not editable here).
          </p>
        </div>
      ) : !canEditRows ? (
        <div className="help-banner help-banner--info geo-directory-master__alert">
          <strong>Read-only copy</strong>
          <p className="geo-directory-master__alert-p">
            The UI is using the bundled directory (no server row identifiers). Start the API so <code>/api/geo/directory</code> returns live rows to
            unlock editing. You can still try <strong>Add district</strong> on the Districts tab if the admin API is running.
          </p>
        </div>
      ) : null}

      <div className="geo-directory-master__tabs" role="tablist" aria-label="Geography level">
        <button
          type="button"
          role="tab"
          id={tabIds.districts}
          aria-selected={tab === "districts"}
          aria-controls="geo-panel-districts"
          className={"geo-directory-master__tab" + (tab === "districts" ? " geo-directory-master__tab--active" : "")}
          onClick={() => setTab("districts")}
        >
          Districts
        </button>
        <button
          type="button"
          role="tab"
          id={tabIds.mandals}
          aria-selected={tab === "mandals"}
          aria-controls="geo-panel-mandals"
          className={"geo-directory-master__tab" + (tab === "mandals" ? " geo-directory-master__tab--active" : "")}
          onClick={() => setTab("mandals")}
        >
          Mandals
        </button>
        <button
          type="button"
          role="tab"
          id={tabIds.villages}
          aria-selected={tab === "villages"}
          aria-controls="geo-panel-villages"
          className={"geo-directory-master__tab" + (tab === "villages" ? " geo-directory-master__tab--active" : "")}
          onClick={() => setTab("villages")}
        >
          Villages
        </button>
      </div>

      {tab === "districts" && (
        <div id="geo-panel-districts" role="tabpanel" aria-labelledby={tabIds.districts} className="geo-directory-master__panels">
          <section className="card card--elevated geo-directory-master__section">
            <header className="card__head card__head--accent">
              <h2 className="geo-directory-master__h2">Add district</h2>
            </header>
            <div className="geo-directory-master__form-grid">
              <div className="field">
                <label htmlFor="geo-new-dk">District key</label>
                <input
                  id="geo-new-dk"
                  value={newDistrictKey}
                  onChange={(e) => setNewDistrictKey(e.target.value)}
                  placeholder="e.g. hyderabad"
                  maxLength={64}
                />
              </div>
              <div className="field">
                <label htmlFor="geo-new-dc">Registration code</label>
                <input
                  id="geo-new-dc"
                  value={newDistrictCode}
                  onChange={(e) => setNewDistrictCode(e.target.value)}
                  placeholder="Official code"
                  maxLength={32}
                />
              </div>
              <div className="field geo-directory-master__form-span">
                <label htmlFor="geo-new-dn">District name</label>
                <input
                  id="geo-new-dn"
                  value={newDistrictName}
                  onChange={(e) => setNewDistrictName(e.target.value)}
                  placeholder="Display name"
                  maxLength={255}
                />
              </div>
            </div>
            <div className="geo-directory-master__form-actions">
              <button
                type="button"
                className="btn btn--primary"
                disabled={!newDistrictKey.trim() || !newDistrictCode.trim() || !newDistrictName.trim()}
                onClick={() => {
                  void (async () => {
                    try {
                      await createGeoAdminDistrict({
                        districtKey: newDistrictKey.trim(),
                        registrationDistrictCode: newDistrictCode.trim(),
                        registrationDistrictName: newDistrictName.trim(),
                      });
                      showToast("District added", "success");
                      setNewDistrictKey("");
                      setNewDistrictCode("");
                      setNewDistrictName("");
                      await onChanged();
                    } catch (e) {
                      showToast(e instanceof Error ? e.message : String(e), "error");
                    }
                  })();
                }}
              >
                Add district
              </button>
            </div>
          </section>

          {districts.length > 0 && canEditRows ? (
            <section className="card card--elevated geo-directory-master__section">
              <header className="card__head">
                <h2 className="geo-directory-master__h2">All districts</h2>
                <span className="geo-directory-master__count">{districts.length}</span>
              </header>
              <div className="geo-directory-master__list">
                {districts.map((d) => (
                  <DistrictEditorCard key={String(districtPrimaryKey(d))} d={d} onChanged={onChanged} />
                ))}
              </div>
            </section>
          ) : districts.length > 0 ? (
            <section className="card card--elevated geo-directory-master__section">
              <header className="card__head">
                <h2 className="geo-directory-master__h2">Districts (read-only)</h2>
              </header>
              <ul className="geo-directory-master__readonly-list">
                {districts.map((d) => (
                  <li key={d.districtKey} className="geo-directory-master__readonly-row">
                    <span className="geo-directory-master__readonly-k">{d.districtKey}</span>
                    <span className="geo-directory-master__readonly-c">{d.registrationDistrictCode}</span>
                    <span className="geo-directory-master__readonly-n">{d.registrationDistrictName}</span>
                  </li>
                ))}
              </ul>
            </section>
          ) : null}
        </div>
      )}

      {tab === "mandals" && (
        <div id="geo-panel-mandals" role="tabpanel" aria-labelledby={tabIds.mandals} className="geo-directory-master__panels">
          {!canEditRows ? (
            <p className="text-muted section-lead">Mandal editing needs live directory data from the API.</p>
          ) : districts.length === 0 ? (
            <p className="text-muted section-lead">Add a district on the Districts tab first.</p>
          ) : (
            <>
              <section className="card card--elevated geo-directory-master__section">
                <header className="card__head card__head--accent">
                  <div>
                    <h2 className="geo-directory-master__h2">Add mandal</h2>
                    <p className="geo-directory-master__section-sub text-muted">Mandals belong to one district.</p>
                  </div>
                </header>
                <div className="field">
                  <label htmlFor="geo-mandal-district">District</label>
                  <select
                    id="geo-mandal-district"
                    className="geo-directory-master__select"
                    value={selectedDistrictId === "" ? "" : String(selectedDistrictId)}
                    onChange={(e) => setSelectedDistrictId(e.target.value === "" ? "" : e.target.value)}
                  >
                    {districts.map((d) => (
                      <option key={d.districtKey} value={String(districtPrimaryKey(d))}>
                        {d.registrationDistrictName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="geo-directory-master__form-grid geo-directory-master__form-grid--2">
                  <div className="field">
                    <label htmlFor="geo-new-mc">Mandal code</label>
                    <input id="geo-new-mc" value={newMandalCode} onChange={(e) => setNewMandalCode(e.target.value)} maxLength={64} />
                  </div>
                  <div className="field">
                    <label htmlFor="geo-new-mn">Mandal name</label>
                    <input id="geo-new-mn" value={newMandalName} onChange={(e) => setNewMandalName(e.target.value)} maxLength={255} />
                  </div>
                </div>
                <div className="geo-directory-master__form-actions">
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={selectedDistrictId === "" || !newMandalCode.trim() || !newMandalName.trim()}
                    onClick={() => {
                      if (selectedDistrictId === "") return;
                      void (async () => {
                        try {
                          await createGeoAdminMandal({
                            districtId: selectedDistrictId,
                            mandalCode: newMandalCode.trim(),
                            mandalName: newMandalName.trim(),
                          });
                          showToast("Mandal added", "success");
                          setNewMandalCode("");
                          setNewMandalName("");
                          await onChanged();
                        } catch (e) {
                          showToast(e instanceof Error ? e.message : String(e), "error");
                        }
                      })();
                    }}
                  >
                    Add mandal
                  </button>
                </div>
              </section>

              {mandalsSorted.length > 0 ? (
                <section className="card card--elevated geo-directory-master__section">
                  <header className="card__head">
                    <h2 className="geo-directory-master__h2">Mandals in {selectedDistrict?.registrationDistrictName ?? "district"}</h2>
                    <span className="geo-directory-master__count">{mandalsSorted.length}</span>
                  </header>
                  <div className="geo-directory-master__list">
                    {selectedDistrictId !== "" &&
                      mandalsSorted.map((m) => (
                        <MandalEditorCard key={m.id} m={m} districtId={selectedDistrictId} onChanged={onChanged} />
                      ))}
                  </div>
                </section>
              ) : (
                <p className="help-banner help-banner--muted geo-directory-master__empty-banner">No mandals for this district yet.</p>
              )}
            </>
          )}
        </div>
      )}

      {tab === "villages" && (
        <div id="geo-panel-villages" role="tabpanel" aria-labelledby={tabIds.villages} className="geo-directory-master__panels">
          {!canEditRows ? (
            <p className="text-muted section-lead">Village editing needs live directory data from the API.</p>
          ) : mandalsSorted.length === 0 || selectedDistrictId === "" ? (
            <p className="text-muted section-lead">Pick a district that has mandals (use the Mandals tab), then return here.</p>
          ) : (
            <>
              <section className="card card--elevated geo-directory-master__section">
                <header className="card__head card__head--accent">
                  <div>
                    <h2 className="geo-directory-master__h2">Add village</h2>
                    <p className="geo-directory-master__section-sub text-muted">
                      District: <strong>{selectedDistrict?.registrationDistrictName}</strong>
                    </p>
                  </div>
                </header>
                <div className="field">
                  <label htmlFor="geo-village-mandal">Mandal</label>
                  <select
                    id="geo-village-mandal"
                    className="geo-directory-master__select"
                    value={selectedMandalId === "" ? "" : String(selectedMandalId)}
                    onChange={(e) => setSelectedMandalId(e.target.value ? Number(e.target.value) : "")}
                  >
                    {mandalsSorted.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.mandalName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="geo-directory-master__form-grid">
                  <div className="field geo-directory-master__form-span">
                    <label htmlFor="geo-new-vn">Village name</label>
                    <input id="geo-new-vn" value={newVillageName} onChange={(e) => setNewVillageName(e.target.value)} maxLength={255} />
                  </div>
                  <div className="field">
                    <label htmlFor="geo-new-vc">Village code</label>
                    <input id="geo-new-vc" value={newVillageCode} onChange={(e) => setNewVillageCode(e.target.value)} maxLength={64} />
                  </div>
                </div>
                <div className="geo-directory-master__form-actions">
                  <button
                    type="button"
                    className="btn btn--primary"
                    disabled={selectedMandalId === "" || !newVillageName.trim() || !newVillageCode.trim()}
                    onClick={() => {
                      if (selectedMandalId === "") return;
                      void (async () => {
                        try {
                          await createGeoAdminVillage({
                            mandalId: selectedMandalId,
                            villageName: newVillageName.trim(),
                            villageCode: newVillageCode.trim(),
                          });
                          showToast("Village added", "success");
                          setNewVillageName("");
                          setNewVillageCode("");
                          await onChanged();
                        } catch (e) {
                          showToast(e instanceof Error ? e.message : String(e), "error");
                        }
                      })();
                    }}
                  >
                    Add village
                  </button>
                </div>
              </section>

              {villagesSorted.length > 0 && selectedMandalId !== "" ? (
                <section className="card card--elevated geo-directory-master__section">
                  <header className="card__head">
                    <h2 className="geo-directory-master__h2">Villages in {selectedMandal?.mandalName ?? "mandal"}</h2>
                    <span className="geo-directory-master__count">{villagesSorted.length}</span>
                  </header>
                  <div className="geo-directory-master__list">
                    {villagesSorted.map((v) => (
                      <VillageEditorCard key={v.id} v={v} mandalId={selectedMandalId} onChanged={onChanged} />
                    ))}
                  </div>
                </section>
              ) : selectedMandalId !== "" ? (
                <p className="help-banner help-banner--muted geo-directory-master__empty-banner">No villages for this mandal yet.</p>
              ) : null}
            </>
          )}
        </div>
      )}
    </div>
  );
}
